<script setup>
import { ref, computed } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import { Button } from '@/Components/ui/button'
import { MessageSquare, CheckCircle2 } from 'lucide-vue-next'
import { trackEvent } from '@/composables/useAnalytics'
import TutorEnquiryModal from '@/Components/Tutors/TutorEnquiryModal.vue'

/**
 * Contact the tutor.
 *
 * Both buttons here used to emit events that no parent listened for, so
 * "Send Message" and "Book Consultation" did nothing on every profile. Booking
 * a consultation does not exist anywhere in the product, so it is gone rather
 * than pointed at something that is not what it says.
 *
 * Messaging splits in two, because the product does:
 *  - A student already subscribed to this tutor has a conversation. The button
 *    opens it.
 *  - Everyone else does not — conversations are only created for an active
 *    Silver or Gold subscription. They get the enquiry form, which reaches the
 *    team through the support channel that already works, and the card says so.
 */
const props = defineProps({
  tutorName: { type: String, required: true },
  tutorSlug: { type: String, required: true },
  tutorId: { type: Number, default: null },
  subscriptions: { type: Array, default: () => [] },
})

const page = usePage()
const showEnquiry = ref(false)

const hasConversation = computed(() =>
  page.props.auth?.user?.user_type === 'student' &&
  props.subscriptions.some((s) => s.teacher_id === props.tutorId)
)

const reassurances = computed(() =>
  hasConversation.value
    ? ['Your thread with this tutor', 'Everything stays inside Bindle']
    : [
        'No commitment and no card needed',
        'Read by the Bindle team, not the tutor',
        'We reply the same working day',
      ]
)

const open = () => {
  trackEvent('contact', { tutor_name: props.tutorName, method: 'enquiry' })
  showEnquiry.value = true
}
</script>

<template>
  <section class="surface-card p-6">
    <div class="text-center">
      <span class="mx-auto grid size-12 place-items-center rounded-full bg-brand-soft text-brand">
        <MessageSquare class="size-6" />
      </span>
      <h2 class="mt-4 font-sans text-base font-bold text-ink">
        Contact {{ tutorName }}
      </h2>
      <p class="mt-1.5 text-sm text-muted">
        <template v-if="hasConversation">
          Pick up where you left off in your messages.
        </template>
        <template v-else>
          Ask anything before you subscribe — we will answer or put you in touch with {{ tutorName }}.
        </template>
      </p>
    </div>

    <Link v-if="hasConversation" href="/student/messages" class="mt-5 block">
      <Button class="w-full" size="lg">Open messages</Button>
    </Link>
    <Button v-else class="mt-5 w-full" size="lg" @click="open">Send a message</Button>

    <ul class="mt-5 space-y-2.5">
      <li v-for="item in reassurances" :key="item" class="flex gap-2.5 text-sm text-muted">
        <CheckCircle2 class="mt-0.5 size-4 shrink-0 text-brand" />
        {{ item }}
      </li>
    </ul>

    <TutorEnquiryModal
      :open="showEnquiry"
      :tutor-name="tutorName"
      :tutor-slug="tutorSlug"
      @close="showEnquiry = false"
    />
  </section>
</template>
